<template>
  <div class="clearfix" v-if="tokens.length">
    <div class="mb-2">
      <b-button size="sm" variant="info" @click="downloadCsv" class="mr-1">
        Download Tokens (.csv)
      </b-button>
      <b-button size="sm" variant="info" @click="removeDivFilter" class="mr-1" :disabled="!divisionFilter">
        Remove Division Filter
      </b-button>
        <span v-if="tokensEditMode && config.editable">
          <button
            @click="saveEdit"
            class="btn-legam-xs mb-2 btn-primary"
            :disabled="Object.keys(tokenActions).length === 0"
          >
            <b-icon icon="lock-fill"></b-icon>
            Save changes
          </button>
          <button @click="lockEdit" class="btn-legam-xs mb-2">
            Cancel
          </button>
        </span>
        <span v-else-if="editMode" id="unlockMessage">
          Save or abort document edits to unlock token editing
        </span>
        <span v-else>
          <button @click="unlockEdit" v-if="$store.getters.checkRole('legam.doc.document.modify') && !editMode" class="btn-legam-xs mb-2">
            <b-icon icon="unlock-fill"></b-icon>
            Unlock editing
          </button>
        </span>

        <div class="search-field-container">
          <div class="search-field">
            <label
              for="tokenSearchField"
              class="label-search">
              Tokens
            </label>
            <input
              id="tokenSearchField"
              v-model.trim="tokenSearchPattern"
              type="text"
              @input="triggerFilterTokens"
              placeholder="search Tokens (RegEx)"
            />
          </div>

          <div class="search-field">
            <label
              for="lemmaSearchField"
              class="label-search">
              Lemmata
            </label>
            <input
              id="lemmaSearchField"
              v-model.trim="lemmaSearchPattern"
              type="text"
              @input="triggerFilterTokens"
              placeholder="search Lemmata (RegEx)"
            />
          </div>

          <div class="search-field">
            <label
              for="hccSearchField"
              class="label-search">
              HCCs
            </label>
            <input
              id="hccSearchField"
              v-model.trim="hccSearchPattern"
              type="text"
              @input="triggerFilterTokens"
              placeholder="search HCCs (RegEx)"
            />
          </div>
        </div>
    </div>

    <b-row class="mt-4">
      <b-col>
        <b-pagination
          v-model="currentPage"
          :total-rows="tokensTable.length"
          :per-page="perPage"
          aria-controls="my-table"
        ></b-pagination>

        <b-table
          class="table"
          ref="tokenTable"
          :fields="fields"
          :items="tokensTable"
          :fixed=true
          :hover=true
          :small=false
          :bordered=false
          :borderless=false
          :per-page="perPage"
          :current-page="currentPage"
          @row-clicked="showDropdown"
          primary-key="id"
          head-variant="light">
          <template #cell(id)="data">
            <tdColUUID
              :value="data.value"
            />
          </template>
          <template #cell(form)="data">
            <span>
              {{ data.value }}
            </span>
          </template>
          <template #cell(div)="data">
            <span v-if="data.value[1]">
              {{ data.value[1] }}
            </span>
            <span v-else>
              {{ data.value[0] }}
            </span>
          </template>
          <template #cell(lemmas)="data">
            <a
              v-for="(lemma, idx) in data.value"
              :key="idx"
              :href="`#/lemma/${lemma[0]}`"
              target="_blank">
              <span>
                {{ lemma[1] }}
              </span>
            </a>
          </template>
          <template #cell(hccs)="data">
            <a
              v-for="(hcc_arr, idx) in data.value"
              :key="idx"
              :href="`#/hcc/${hcc_arr[0]}`"
              target="_blank">
              <span :title="hcc_arr[2]">
                {{ hcc_arr[1] }}
              </span>
            </a>
          </template>
        </b-table>

        <b-pagination
          v-model="currentPage"
          :total-rows="tokensTable.length"
          :per-page="perPage"
          aria-controls="my-table"
        ></b-pagination>
      </b-col>
    </b-row>

    <b-dropdown
      v-if="actionDropdownVisible"
      no-caret
      ref="actionDropdown"
      variant="link"
      :style="{ position: 'fixed', top: dropdownPosition.y + 'px', left: dropdownPosition.x + 'px' }"
      @hide="actionDropdownVisible = false"
    >
      <b-dropdown-item @click="performAction('edit')">Edit</b-dropdown-item>
      <b-dropdown-item @click="performAction('delete')">Delete</b-dropdown-item>
      <b-dropdown-item @click="performAction('split')">Split</b-dropdown-item>
      <b-dropdown-item @click="performAction('insertBefore')">Insert Token before</b-dropdown-item>
      <b-dropdown-item @click="performAction('insertAfter')">Insert Token after</b-dropdown-item>
      <hr>
      <b-dropdown-item @click="tagToken('lemmatize')">Lemmatize</b-dropdown-item>
      <b-dropdown-item @click="tagToken('tag')">Tag (HCCs)</b-dropdown-item>
    </b-dropdown>

    <b-dropdown
      v-if="viewDropdownVisible"
      no-caret
      ref="viewDropdown"
      variant="link"
      :style="{ position: 'fixed', top: dropdownPosition.y + 'px', left: dropdownPosition.x + 'px' }"
      @hide="viewDropdownVisible = false"
    >
      <b-dropdown-item @click="jumpToToken">Jump to Token</b-dropdown-item>
    </b-dropdown>

    <b-dropdown
      v-if="revertDropdownVisible"
      no-caret
      ref="revertDropdown"
      variant="link"
      :style="{ position: 'fixed', top: dropdownPosition.y + 'px', left: dropdownPosition.x + 'px' }"
      @hide="revertDropdownVisible = false"
    >
      <b-dropdown-item @click="performAction('revert')">Revert</b-dropdown-item>
    </b-dropdown>

    <b-modal
      v-if="config.editable"
      id="modal-insert-token-xml"
      :title="`new Token ${insertPosition} '${editToken.form}'`"
      ok-variant="secondary"
      size="lg"
      @ok="saveInsertToken"
      ok-title="OK"
      :ok-disabled="editInsertToken.form && !validateInput(editInsertToken.form)"
    >
      <div class="d-block text-center">
        Add multiple Tokens at once by separating them with white space.<br>make sure to wrap punctuation and numerals with the appropriate tags:
        <div>
         <p id="insert-example">le &lt;num&gt;I&lt;/num&gt; test&lt;punct original="."&gt;.&lt;/punct&gt;</p>
        </div>
      </div>

      <LanguageSelect
        :language="language"
        :multiple="false"
        @select="language => selectLanguage(language, 'insert')"
      />

      <b-form-group
        v-if="editInsertToken"
        id="token-xml-group"
        label="Token XML"
        label-for="token-xml"
      >
        <b-form-input
          id="token-xml"
          v-model.trim.lazy="editInsertToken.form"
          placeholder="new Token"
        ></b-form-input>
      </b-form-group>
    </b-modal>


    <b-modal
      v-if="config.editable"
      id="modal-edit-token-xml"
      title="Edit token XML"
      ok-variant="secondary"
      :ok-disabled="editToken.newForm && !validateInput(editToken.newForm)"
      size="lg"
      @ok="saveToken"
      ok-title="OK"
    >
      <LanguageSelect
        :language="editToken.languages"
        :multiple="true"
        @select="language => selectLanguage(language, 'edit')"
      />

      <b-form-group
        v-if="editToken"
        id="token-xml-group"
        label="Token XML"
        label-for="token-xml"
      >
        <b-form-input
          id="token-xml"
          v-model.trim.lazy="editToken.newForm"
          placeholder="editToken.form"
        ></b-form-input>
      </b-form-group>
    </b-modal>


    <b-modal
      v-if="config.editable"
      id="modal-tag-token-lemma"
      title="Lemmatize Token"
      ok-variant="secondary"
      size="lg"
      @ok="saveLemmatizedToken"
      ok-title="OK"
    >
      <LemmaSelect
        :lemma="tokenLemmas.map(x => {return {id: x.id, display: x.lemma}})"
        :isRequired="true"
        :isArticleEtymon="false"
        :multiple="true"
        ref="lemmaSelect"
        @select="selectLemma"
      />
    </b-modal>


    <b-modal
      v-if="config.editable"
      id="modal-tag-token-hcc"
      title="Tag Token (HCC)"
      ok-variant="secondary"
      size="lg"
      @ok="saveTaggedToken"
      ok-title="OK"
    >
      <HCCSelect
        :hccs="selectedRow.hccs.map(x => {return {id: x[0], code: x[1], shortname: x[2]}})"
        ref="hccSelect"
      />
    </b-modal>


    <b-modal
      v-if="config.editable"
      id="modal-split-token-xml"
      title="Split token XML"
      ok-variant="secondary"
      size="lg"
      @ok="saveSplitToken"
      ok-title="OK"
    >
      <div class="text-center split-text" v-if="editToken && config.editable">
        <span
          v-for="(letter, index) in editToken.form.split('')"
          :key="'tst-' + index"
          @click="(index > -1 && index < (editToken.form.length - 1)) ? splitIndex = index : splitIndex"
        >{{ letter }}<span class="legam-split-divider" v-if="splitIndex == index">|</span></span>
      </div>
      <div class="text-center">
        keep annotations etc. on part
        <b-form-radio-group v-model="selectedAB">
          <b-form-radio value="a">a</b-form-radio>
          <b-form-radio value="b">b</b-form-radio>
        </b-form-radio-group>
      </div>
    </b-modal>
  </div>
</template>

<style scoped>
  * >>> .tdAction {
    width: 10px !important;
  }

  * >>> .tdToken {
    width: 600px !important;
  }

  * >>> #unlockMessage {
    background-color: lightgoldenrodyellow;
    border-radius: 3px;
  }

  .search-field-container {
    display: flex;
    gap: 20px;
  }

  .search-field {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    flex: 1;
  }

  .label-search {
    margin-bottom: 5px;
    font-weight: bold;
  }

  .legam-split-divider {
    padding: 0px 5px;
    color: red;
    font-size: 40px;
  }
  .split-text {
    text-align: center;
    margin-bottom: 10px;
    font-size: 35px;
  }
  .text-center {
    text-align: center;
  }
  .text-center span {
    cursor: s-resize;
  }

  * >>> .highlighted-row-delete {
    background-color: #ff0000;
    opacity: 30%;
  }

  * >>> .highlighted-row-edit {
    background-color: #c2eabd;
  }

  * >>> .highlighted-row-insert {
    background-color: #afabd9;
  }

  * >>> .highlighted-row-split {
    background-color: #dcf2b0;
  }

  * >>> .modal-content {
    white-space: pre;
  }

  .d-block {
    padding-bottom: 40px;
  }

  #insert-example {
    font-family: 'Lucida Console',monospace;
    display: inline;
    background-color: gold;
    padding: 2px;
  }

  /* table columns width */
  * >>> th[aria-colindex="1"] {
    width: 100px;
  }

  * >>> th[aria-colindex="2"] {
    width: 120px;
  }

  * >>> th[aria-colindex="3"] {
    width: 120px;
  }

  * >>> th[aria-colindex="5"] {
    width: 120px;
  }

  * >>> th[aria-colindex="6"] {
    width: 200px;
  }
</style>

<script>
  import axios from "axios"
  import tdColUUID from '@/components/tdColUUID.vue'
  import config from '@/config.js'
  import Vue from 'vue'
  import LemmaSelect from '@/components/Lemma/LemmaSelect.vue'
  import HCCSelect from "@/components/Scripta/HCCSelect.vue"

  import LanguageSelect from '@/components/Main/LanguageSelect'

  import 'datatables.net-bs4'
  import 'datatables.net-bs4/css/dataTables.bootstrap4.css'
  import '@/utilities/datatables-pagination.js'
  import '@/utilities/datatables-pagination.css'
  import { XMLStringIsWellFormed } from '@/utilities/xml-functions.js'

  const API_URL = config.apiUrl

  export default {
    name: 'TokenTable',
    props: ['tokens', 'divisionFilter', 'documentId', 'editMode', 'copyTokenActions'],
    data(){
      return {
        tokensEditMode: Object.keys(this.copyTokenActions).length > 0  ? true : false,
        tokenActions: {...this.copyTokenActions},
        pseudoTokenId: 1,
        tokenLemmas: [],
        tempLemmata: [],
        tempHCCs: [],
        config: config,
        actionDropdownVisible: false,
        revertDropdownVisible: false,
        viewDropdownVisible: false,
        dropdownPosition: { x: 0, y: 0 },
        selectedRow: {lemmas: [], hccs: []},
        editToken: {form: '', lang: null},
        editInsertToken: {form: null},
        selectedAB: 'b',
        tokenSearchPattern: "",
        lemmaSearchPattern: "",
        hccSearchPattern: "",
        splitIndex: 0,
        fields: [
          { key: "id", label: "ID"},
          { key: "idx", label: "Index", sortable: true},
          { key: "div", label: "Division"},
          { key: "form", label: "Token", sortable: true},
          { key: "lang", label: "Language"},
          { key: "lemmas", label: "Lemmata", sortable: true},
          { key: "hccs", label: "HCCs", sortable: true},
        ],
        backupTokensTable: null,
        tokensTable: this.tokens.filter(token => this.divisionFilter ? token.division == this.divisionFilter : true).map(token => { return {
          id: token.id,
          idx: token.idx+1,
          div: [token.division, token.content],
          form: this.computeForm(token.id, token.formDoc),
          newForm: token.formDoc,
          // formDoc: token.formDoc,
          lang: token.tokenLanguage ? token.tokenLanguage.join(", ") : "",
          lemmas: token.lemmas ?? [],
          hccs: token.hccs ?? []
        }}),
        perPage: 100,
        currentPage: 1,
        key: 1,
        searchableByColumn: true,
        additionalSettings: {
          order: [[1, 'asc']]
        },
        language: null,
        insertPosition: null
      }
    },
    mounted() {
      this.computeTokens()
    },
    methods: {
      onlyUnique(value, index, array) {
        return array.map(x => x.id).indexOf(value.id) === index;
      },
      selectLemma(event) {
        if (event) this.tempLemmata.push({id: event.id, display: event.lemmaForm})
      },
      async getLemmata(lemmata) {
        let fullLemmata = []

        for await (const lemma of lemmata) {
          await axios
            .get(config.apiUrl + `/lemmas/autocomplete?lemmaId=${lemma[0]}`)
            .then((response) => {
              fullLemmata.push(response.data.results[0])
            })
        }

        return fullLemmata
      },
      async tagToken(action) {
        if (action == "lemmatize") {
          this.selectedRow.lemmas.forEach(x => {
            this.tempLemmata.push({id: x[0], display: x[1]})
          })
          this.tokenLemmas = await this.getLemmata(this.selectedRow.lemmas)
          this.$bvModal.show('modal-tag-token-lemma')
        } else if (action == "tag") {
          this.selectedRow.hccs.forEach(x => {
            this.tempHCCs.push({id: x[0], display: x[1]})
          })
          this.$bvModal.show('modal-tag-token-hcc')
        }
      },
      computeTokens() {
        if (Object.keys(this.tokenActions).length > 0) {
          Object.values(this.tokenActions)
            .filter(action => Object.keys(action)[0] == "insert")
            .forEach(action => {
              const actionObj = Object.values(action)[0]
              let idx         = this.tokensTable.map(elem => elem.id).indexOf(actionObj.tokenId)
              if (actionObj.position == "after") idx++

              if (idx > -1) {
                this.tokensTable = this.tokensTable.slice(0, idx)
                  .concat({
                    id: this.pseudoTokenId.toString(),
                    form: actionObj.xml,
                    tokenId: actionObj.tokenId,
                    position: actionObj.position,
                    idx: this.tokensTable[idx].idx + actionObj.xml.length,
                    div: this.tokensTable[idx].div
                  })
                  .concat(this.tokensTable.slice(idx))

                this.pseudoTokenId++
              }
          })
          this.highlightTokens()
        }
      },
      highlightTokens() {
        Object.entries(this.tokenActions).forEach(([id, action]) => {
          const actionName = Object.keys(action)[0]
          if (actionName == 'edit') {
            this.setCSSclass(id, "highlighted-row-edit")
          } else if (actionName == 'delete') {
            this.setCSSclass(id, "highlighted-row-delete")
          } else if (actionName == 'split') {
            this.setCSSclass(id, "highlighted-row-split")
          }

          if (this.pseudoTokenId > 1) {
            let i = 1
            while (i <= this.pseudoTokenId) {
              this.setCSSclass(i, "highlighted-row-insert")
              i++
            }
          }
        })
      },
      computeForm(tokenId, tokenForm) {
        if (Object.keys(this.copyTokenActions).includes(tokenId)) {
          const type   = Object.keys(this.copyTokenActions[tokenId])[0]
          const action = Object.values(this.copyTokenActions[tokenId])[0]

          if (type == 'edit') {
            return `${action.oldForm} ☞ ${action.xml}`
          } else if (type == 'delete') {
            return tokenForm
          } else if (type == 'split') {
            let head = action.oldForm.slice(0, action.index)
            let tail = action.oldForm.slice(action.index)

            if (action.selectedAB === "a") {
              head = `<u protect="true">${head}</u>`
            } else {
              tail = `<u protect="true">${tail}</u>`
            }

            return head + "✂" + tail
          } else if (type == 'insertBefore') {
            return tokenForm
          } else if (type == 'insertAfter') {
            return tokenForm
          }
        } else {
          return tokenForm
        }
      },
      validateInput(input) {
        return input.length > 0 && XMLStringIsWellFormed(input)
      },
      selectLanguage(language, target) {

        if (target == 'insert') {
          this.language = language
          this.editInsertToken.lang = language.code
        } else if (target == 'edit') {
          this.editToken.languages = language
          const langs = this.editToken.languages.map(lang => lang.code).join(', ')
          this.editToken.lang = langs
          this.selectedRow.lang = langs
        }
      },
      triggerFilterTokens() {
        clearTimeout(this.timeout);

        if (! this.backupTokensTable) {
          this.backupTokensTable = this.tokensTable
        }

        this.timeout = setTimeout(() => {
          this.filterTokens();
        }, 300);
      },
      filterTokens() {
        const tokenPattern = new RegExp(this.tokenSearchPattern, "i")
        const lemmaPattern = new RegExp(this.lemmaSearchPattern, "i")
        const hccPattern   = new RegExp(this.hccSearchPattern, "i")

        this.tokensTable = this.backupTokensTable
                              .filter(elem => elem.form.replace(/<[^>]+>/g, "").match(tokenPattern))
        if (this.lemmaSearchPattern) {
          this.tokensTable = this.tokensTable
                              .filter(elem => elem.lemmas)
                              .filter(elem => elem.lemmas.some(lemma => lemma[1].match(lemmaPattern)))
        }
        if (this.hccSearchPattern) {
          this.tokensTable = this.tokensTable
                              .filter(elem => elem.hccs)
                              .filter(elem => elem.hccs.some(hccs => hccs[1].match(hccPattern) || hccs[2].match(hccPattern)))
        }

        this.highlightTokens()
      },
      unlockEdit() {
        this.tokensEditMode = true
      },
      lockEdit() {
        this.tokensEditMode = false

        const restoreTokensTable = this.tokensTable.filter(elem => ! Object.keys(elem).includes("position")).map(elem => {
          let restoreElem

          if (Object.keys(this.tokenActions).includes(elem.id)) {
            const action = Object.keys(this.tokenActions[elem.id])[0]

            if (action !== "insert") {
              this.unSetCSSclass(elem.id)
            }

            if (action === "edit") {
              const oldForm = this.tokenActions[elem.id].edit.oldForm
              restoreElem = {...elem, form: oldForm, newForm: oldForm}
            } else if (action === "split") {
              const oldForm = elem.form.replace(/<\/?u(?: protect="true")?>|✂/g, "")
              restoreElem = {...elem, form: oldForm, newForm: oldForm}
            } else if (action === "lemmatize") {
              const lemmas = this.tokenActions[elem.id].lemmatize.preLemmata
              restoreElem = {...elem, lemmas: lemmas}
            } else if (action === "tag") {
              const hccs = this.tokenActions[elem.id].lemmatize.preHCCs
              restoreElem = {...elem, hccs: hccs}
            }

            return restoreElem
          }

          return elem
        })

        this.tokensTable   = restoreTokensTable
        this.tokenActions  = {}
        this.pseudoTokenId = 1
      },
      removeDivFilter() {
        this.$emit('removeDivFilter')
      },
      saveEdit() {
        const numToks = Object.keys(this.tokenActions).length
        this.$bvModal.msgBoxConfirm(`${numToks}  ${numToks === 1 ? 'Token' : 'Tokens'} will be modified - continue?`)
        .then(value => {
          if (value){
            this.$store.dispatch('batchTokenActions', this.tokenActions).then(() => {
              this.$emit('refresh')
              this.$store.dispatch('addNotification', {
                type: 'success',
                text: `Token changes applied`
              })
            })
          }
        })
      },
      downloadCsv() {
        const rows = [
          ["Index", "Division", "Token", "Language", "Lemmas", "HCCs"],
          ...this.tokensTable.map(row => [
            row.idx,
            row.div,
            row.form,
            row.lang,
            row.lemmas ? row.lemmas.map(lemma => lemma[1]).join('; ') : "",
            row.hccs ? row.hccs.map(hcc => hcc[1]).join('; ') : "",
          ])
        ]

        // let csvContent = "data:text/csv;charset=utf-8," + rows.map(e => e.join("\t")).join("\n");
        let csvContent = "data:text/csv;charset=utf-8," + this.toCSV(rows);
        let encodedUri = encodeURI(csvContent);
        let filename = `gallrom_export_${(new Date()).toISOString()}.csv`;
        let link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
      },
      toCSV(table) {
        return table
          .map(row => row.map(cell => {
              // We remove blanks and check if the column contains
              // other whitespace,`,` or `"`.
              // In that case, we need to quote the column.
              cell = cell ? cell.toString().replace(/#/g, ".") : ''
              if (cell.replace(/ /g, '').match(/[\s,"]/)) {
                  return '"' + cell.replace(/"/g, '""') + '"';
              }
              return cell;
            }).join(',')
          )
          .join('\n');
      },
      showDropdown(item, index, event) {
        this.selectedRow = item

        this.dropdownPosition = {
          x: event.clientX,
          y: event.clientY
        }

        if (this.tokensEditMode) {
          if (! Object.keys(this.tokenActions).includes(item.id)) {
            this.actionDropdownVisible = true
            this.$nextTick(() => {
              if (this.$refs.actionDropdown) {
                this.$refs.actionDropdown.show()
              }
            })
          } else {
            this.revertDropdownVisible = true
            this.$nextTick(() => {
              if (this.$refs.revertDropdown) {
                this.$refs.revertDropdown.show()
              }
            })
          }
        } else {
          this.viewDropdownVisible = true
          this.$nextTick(() => {
            if (this.$refs.viewDropdown) {
              this.$refs.viewDropdown.show()
            }
          })
        }
      },
      async setCSSclass(rowId, styleClass) {
        const targetNode = document.body
        const config     = { childList: true, subtree: true }

        return new Promise((resolve) => {
          const observer = new MutationObserver(() => {
            const element = document.evaluate(
              `//tr[@data-pk='${rowId}']`,
              document,
              null,
              XPathResult.FIRST_ORDERED_NODE_TYPE,
              null
            ).singleNodeValue

            if (element) {
              element.classList.add(styleClass)
              observer.disconnect()
              resolve()
            }
          })

          observer.observe(targetNode, config)
        })
      },
      unSetCSSclass(rowId) {
        document.evaluate(`//tr[@data-pk='${rowId}']`, document, null, XPathResult.FIRST_ORDERED_NODE_TYPE, null).singleNodeValue.classList.remove("highlighted-row-delete", "highlighted-row-edit", "highlighted-row-split")
      },
      jumpToToken() {
        this.$emit("tokenJump", this.selectedRow.id)
      },
      async performAction(action) {
        this.editToken = {languages: [], ...this.selectedRow}

        if (action === "edit") {
          if (this.editToken.lang) {
            const responses = await Promise.all(
              this.editToken.lang.split(",").map(
                async langCode => {
                  const res = await this.getLanguage(langCode.trim())
                  return res
                }
              )
            )
            this.editToken.languages.push(...responses)
          }

          this.$bvModal.show('modal-edit-token-xml')
        } else if (action === "delete") {
          Vue.set(this.tokenActions, this.editToken.id, {"delete": {"docId": this.documentId}})
          this.setCSSclass(this.editToken.id, "highlighted-row-delete")
        } else if (action === "split") {
          this.$bvModal.show('modal-split-token-xml')
        } else if (action === "insertBefore") {
          this.insertPosition = "before"
          this.editInsertToken = {
            id: this.pseudoTokenId.toString(),
            idx: this.editToken.idx,
            div: this.editToken.div,
            form: "",
            lang: this.language?.code,
            position: "before",
            tokenId: this.editToken.id
          }
          this.pseudoTokenId++
          this.$bvModal.show('modal-insert-token-xml')
        } else if (action === "insertAfter") {
          this.insertPosition = "after"
          this.editInsertToken = {
            id: this.pseudoTokenId.toString(),
            idx: this.editToken.idx + this.editToken.form.length + 1,
            div: this.editToken.div,
            form: "",
            lang: this.language?.code,
            position: "after",
            tokenId: this.editToken.id
          }
          this.pseudoTokenId++
          this.$bvModal.show('modal-insert-token-xml')
        } else if (action === "revert") {
          const tokenAction = Object.keys(this.tokenActions[this.editToken.id])[0]
          let idx

          if (tokenAction === "edit" || tokenAction === "split") {
            idx           = this.tokensTable.map(elem => elem.id).indexOf(this.editToken.id)
            const oldForm = Object.values(this.tokenActions[this.editToken.id])[0].oldForm
            // this is needed, so that Vue knows it needs to update DOM
            this.$set(this.tokensTable, idx, {...this.tokensTable[idx], form: oldForm, newForm: oldForm})
          } else if (tokenAction === "insert") {
            idx = this.tokensTable.map(elem => elem.id).indexOf(this.editToken.id)
            this.tokensTable = this.tokensTable.slice(0, idx).concat(this.tokensTable.slice(idx + 1))
          } else if (tokenAction === "lemmatize") {
            const lemmas = Object.values(this.tokenActions[this.editToken.id])[0].preLemmata
            idx          = this.tokensTable.map(elem => elem.id).indexOf(this.editToken.id)
            this.$set(this.tokensTable, idx, {...this.tokensTable[idx], lemmas: lemmas})
          } else if (tokenAction === "tag") {
            const hccs = Object.values(this.tokenActions[this.editToken.id])[0].preHCCs
            idx        = this.tokensTable.map(elem => elem.id).indexOf(this.editToken.id)
            this.$set(this.tokensTable, idx, {...this.tokensTable[idx], hccs: hccs})
          }

          Vue.delete(this.tokenActions, this.editToken.id)
          if (tokenAction !== "insert") {
            this.unSetCSSclass(this.editToken.id)
          }
        }
        this.actionDropdownVisible = false;
        this.revertDropdownVisible = false;
      },
      saveSplitToken() {
        const form = this.editToken.form
        const idx  = this.splitIndex + 1

        let head = form.slice(0, idx)
        let tail = form.slice(idx)

        Vue.set(this.tokenActions, this.editToken.id, {
          "split": {
            "index": idx,
            "splitOff": this.selectedAB,
            "oldForm": this.editToken.form,
            "docId": this.documentId,
          }
        })
        // TODO: indicate on which part the annos stay
        if (this.selectedAB === "a") {
          head = `<u protect="true">${head}</u>`
        } else {
          tail = `<u protect="true">${tail}</u>`
        }

        this.editToken.form   = head + "✂" + tail
        this.selectedRow.form = this.editToken.form.replace(/<\/?u(?: protect="true")?>/g, "")
        this.setCSSclass(this.editToken.id, "highlighted-row-split")
      },
      saveLemmatizedToken() {
        const preLemmata        = [...this.selectedRow.lemmas]
        this.selectedRow.lemmas = this.tempLemmata.filter(
                                    this.onlyUnique
                                  ).filter(
                                    x => this.$refs.lemmaSelect.selected.includes(x.id)
                                  ).map(x => {return [x.id, x.display]})
        Vue.set(this.tokenActions, this.selectedRow.id, {
          "lemmatize": {
            "lemmaIds": this.selectedRow.lemmas.map(x => x[0]),
            "docId": this.documentId,
            "preLemmata": preLemmata
          }
        })

        this.setCSSclass(this.selectedRow.id, "highlighted-row-edit")
      },
      saveTaggedToken() {
        const preHCCs         = [...this.selectedRow.hccs]
        this.selectedRow.hccs = this.$refs.hccSelect.hccsArr.map(x => {return [x.id, x.code, x.shortname]})

        Vue.set(this.tokenActions, this.selectedRow.id, {
          "tag": {
            "hccIds": this.selectedRow.hccs.map(x => x[0]),
            "docId": this.documentId,
            "preHCCs": preHCCs
          }
        })

        this.setCSSclass(this.selectedRow.id, "highlighted-row-edit")
      },
      saveToken() {
        Vue.set(this.tokenActions, this.editToken.id, {
          "edit": {
            "xml": this.editToken.newForm,
            "oldForm": this.editToken.form,
            "docId": this.documentId,
            "language": this.editToken.lang
          }
        })
        this.selectedRow.form = `${this.editToken.form} ☞ ${this.editToken.newForm}`
        this.setCSSclass(this.editToken.id, "highlighted-row-edit")
      },
      saveInsertToken(){
        Vue.set(this.tokenActions, this.editInsertToken.id, {
          "insert": {
            "xml": this.editInsertToken.form,
            "docId": this.documentId,
            "language": this.language?.code,
            "tokenId": this.editInsertToken.tokenId,
            "position": this.editInsertToken.position
          }
        })
        let idx = this.tokensTable.map(elem => elem.id).indexOf(this.editToken.id)
        if (this.editInsertToken.position == "after") idx++
        this.tokensTable = this.tokensTable.slice(0, idx)
          .concat(this.editInsertToken)
          .concat(this.tokensTable.slice(idx))

        this.setCSSclass(this.editInsertToken.id, "highlighted-row-insert")
      },
      async getLanguage(code) {
        const response = await axios.get(`${API_URL}/languages/${code}`)
        return response.data
      }
    },
    components: {
      tdColUUID,
      LanguageSelect,
      LemmaSelect,
      HCCSelect
    },
    async created() {
      window.addEventListener('keydown', (e) => {
        if (this.editToken && this.editToken.form){
          if (e.key == 'ArrowLeft') {
            this.splitIndex--
            if (this.splitIndex < 0) {
              this.splitIndex = 0
            }
          }
          else if (e.key == 'ArrowRight') {
            this.splitIndex++
            if (this.splitIndex > (this.editToken.form.length - 2)) {
              this.splitIndex = this.editToken.form.length - 2
            }
          }
        }
      });
      let langObj = {}

      this.tokens.map(
        token => token.tokenLanguage
      ).flat().map(
        lang => lang in langObj ? langObj[lang]++ : langObj[lang] = 1
      )

      if (Object.keys(langObj).length) {
        let langCode = Object.entries(langObj).sort((x, y) => x[1] > y[1] ? -1 : 1)[0][0]
        this.language = await this.getLanguage(langCode)
      }
    },
  }
</script>
